import React, { useMemo, useState } from 'react';
import { Badge, Card, Nav, Tab } from 'react-bootstrap';
import { FiCloudRain, FiEye } from 'react-icons/fi';
import StandardCheckForecastTab from '../../components/Admin/StandardCheckForecastTab';
import StandardBulkObservationCheckTab from '../../components/Admin/StandardBulkObservationCheckTab';
import {
    checkStandardFallbackTripsForecasts,
    checkStandardScheduledBulkObservationFallback,
} from '../../services/api';
import { useTranslation } from '../../hooks/useTranslation';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminTripsDaily.css';

const MAIN_GROUPS = {
    FORECASTS: 'forecasts',
    OBSERVATIONS: 'observations',
};

const SCOPE_VARIANTS = {
    STANDARD: 'standard',
    SPECIFIC: 'specific',
};

const SLOT_STATUS = {
    AVAILABLE: 'available',
    SOON: 'soon',
};

const TABS = {
    CHECK_FORECAST_STANDARD: 'check-forecast-standard',
    CHECK_FORECAST_FALLBACK: 'check-forecast-standard-fallback',
    SPECIFIC_PLACEHOLDER: 'specific-placeholder',
    BULK_OBSERVATION_STANDARD: 'bulk-observation-standard',
    BULK_OBSERVATION_FALLBACK: 'bulk-observation-standard-fallback',
    OBSERVATION_SPECIFIC_PLACEHOLDER: 'observation-specific-placeholder',
};

const STANDARD_FORECAST_TOOLS = [
    {
        key: TABS.CHECK_FORECAST_STANDARD,
        labelKey: 'sidebar.weatherDailyAnalysisMenu.checkForecastStandard',
    },
    {
        key: TABS.CHECK_FORECAST_FALLBACK,
        labelKey: 'sidebar.weatherDailyAnalysisMenu.checkForecastFallback',
    },
];

const STANDARD_OBSERVATION_TOOLS = [
    {
        key: TABS.BULK_OBSERVATION_STANDARD,
        labelKey: 'sidebar.weatherDailyAnalysisMenu.bulkObservationStandard',
    },
    {
        key: TABS.BULK_OBSERVATION_FALLBACK,
        labelKey: 'sidebar.weatherDailyAnalysisMenu.bulkObservationFallback',
    },
];

const FORECAST_SLOT_MAP = {
    [SCOPE_VARIANTS.STANDARD]: {
        tab: TABS.CHECK_FORECAST_STANDARD,
        status: SLOT_STATUS.AVAILABLE,
        tools: STANDARD_FORECAST_TOOLS,
    },
    [SCOPE_VARIANTS.SPECIFIC]: {
        tab: TABS.SPECIFIC_PLACEHOLDER,
        status: SLOT_STATUS.SOON,
    },
};

const OBSERVATION_SLOT_MAP = {
    [SCOPE_VARIANTS.STANDARD]: {
        tab: TABS.BULK_OBSERVATION_STANDARD,
        status: SLOT_STATUS.AVAILABLE,
        tools: STANDARD_OBSERVATION_TOOLS,
    },
    [SCOPE_VARIANTS.SPECIFIC]: {
        tab: TABS.OBSERVATION_SPECIFIC_PLACEHOLDER,
        status: SLOT_STATUS.SOON,
    },
};

const AdminWeatherDailyAnalysis = () => {
    const { t } = useTranslation();
    const [mainGroup, setMainGroup] = useState(MAIN_GROUPS.FORECASTS);
    const [forecastVariant, setForecastVariant] = useState(SCOPE_VARIANTS.STANDARD);
    const [observationVariant, setObservationVariant] = useState(SCOPE_VARIANTS.STANDARD);
    const [activeTab, setActiveTab] = useState(TABS.CHECK_FORECAST_STANDARD);

    const currentSlot = useMemo(() => {
        if (mainGroup === MAIN_GROUPS.OBSERVATIONS) {
            return OBSERVATION_SLOT_MAP[observationVariant]
                || OBSERVATION_SLOT_MAP[SCOPE_VARIANTS.STANDARD];
        }
        return FORECAST_SLOT_MAP[forecastVariant]
            || FORECAST_SLOT_MAP[SCOPE_VARIANTS.STANDARD];
    }, [mainGroup, forecastVariant, observationVariant]);

    const selectMainGroup = (group) => {
        setMainGroup(group);
        if (group === MAIN_GROUPS.OBSERVATIONS) {
            const slot = OBSERVATION_SLOT_MAP[observationVariant]
                || OBSERVATION_SLOT_MAP[SCOPE_VARIANTS.STANDARD];
            setActiveTab(slot.tab);
            return;
        }
        const slot = FORECAST_SLOT_MAP[forecastVariant]
            || FORECAST_SLOT_MAP[SCOPE_VARIANTS.STANDARD];
        setActiveTab(slot.tab);
    };

    const selectVariant = (variant) => {
        if (mainGroup === MAIN_GROUPS.OBSERVATIONS) {
            const slot = OBSERVATION_SLOT_MAP[variant]
                || OBSERVATION_SLOT_MAP[SCOPE_VARIANTS.STANDARD];
            setObservationVariant(variant);
            setActiveTab(slot.tab);
            return;
        }
        const slot = FORECAST_SLOT_MAP[variant]
            || FORECAST_SLOT_MAP[SCOPE_VARIANTS.STANDARD];
        setForecastVariant(variant);
        setActiveTab(slot.tab);
    };

    const activeVariant = mainGroup === MAIN_GROUPS.OBSERVATIONS
        ? observationVariant
        : forecastVariant;

    return (
        <div className="dashboard-page admin-trips-daily-page">
            <Card>
                <Card.Body>
                    <header className="tn-page-header">
                        <h1 className="tn-page-title">
                            <FiCloudRain size={28} aria-hidden />
                            {t('sidebar.weatherDailyAnalysis')}
                        </h1>
                        <p className="tn-page-subtitle">
                            {t('pages.adminWeatherDailyAnalysis.subtitle')}
                        </p>
                    </header>

                    <Nav variant="tabs" className="tn-main-tabs" role="tablist">
                        <Nav.Item>
                            <Nav.Link
                                active={mainGroup === MAIN_GROUPS.FORECASTS}
                                onClick={() => selectMainGroup(MAIN_GROUPS.FORECASTS)}
                                role="tab"
                                aria-selected={mainGroup === MAIN_GROUPS.FORECASTS}
                            >
                                {t('sidebar.weatherDailyAnalysisMenu.forecasts')}
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link
                                active={mainGroup === MAIN_GROUPS.OBSERVATIONS}
                                onClick={() => selectMainGroup(MAIN_GROUPS.OBSERVATIONS)}
                                role="tab"
                                aria-selected={mainGroup === MAIN_GROUPS.OBSERVATIONS}
                            >
                                {t('sidebar.weatherDailyAnalysisMenu.observations')}
                            </Nav.Link>
                        </Nav.Item>
                    </Nav>

                    <Nav
                        className="tn-segmented"
                        role="tablist"
                        aria-label={t('pages.adminTripsDaily.variants.label')}
                    >
                        {[SCOPE_VARIANTS.STANDARD, SCOPE_VARIANTS.SPECIFIC].map((variantKey) => (
                            <Nav.Item key={variantKey}>
                                <Nav.Link
                                    active={activeVariant === variantKey}
                                    onClick={() => selectVariant(variantKey)}
                                    role="tab"
                                    aria-selected={activeVariant === variantKey}
                                >
                                    {t(`sidebar.weatherDailyAnalysisMenu.${variantKey}`)}
                                </Nav.Link>
                            </Nav.Item>
                        ))}
                    </Nav>

                    {Array.isArray(currentSlot.tools) && currentSlot.tools.length > 0 && (
                        <Nav className="tn-tools-nav" role="tablist">
                            {currentSlot.tools.map((tool) => (
                                <Nav.Item key={tool.key}>
                                    <Nav.Link
                                        active={activeTab === tool.key}
                                        onClick={() => setActiveTab(tool.key)}
                                        role="tab"
                                        aria-selected={activeTab === tool.key}
                                    >
                                        {t(tool.labelKey)}
                                    </Nav.Link>
                                </Nav.Item>
                            ))}
                        </Nav>
                    )}

                    <Tab.Container
                        activeKey={activeTab}
                        onSelect={(key) => setActiveTab(key || currentSlot.tab)}
                    >
                        <Tab.Content>
                            <Tab.Pane eventKey={TABS.CHECK_FORECAST_STANDARD}>
                                <StandardCheckForecastTab />
                            </Tab.Pane>
                            <Tab.Pane eventKey={TABS.CHECK_FORECAST_FALLBACK}>
                                <StandardCheckForecastTab
                                    prefix="pages.adminTripsDaily.standardFallbackCheckForecast"
                                    runRequest={checkStandardFallbackTripsForecasts}
                                    sourceField="forecast_data_source_fallback"
                                />
                            </Tab.Pane>
                            <Tab.Pane eventKey={TABS.SPECIFIC_PLACEHOLDER}>
                                <div className="tn-slot-placeholder">
                                    <Badge bg="secondary" className="tn-status-badge mb-3">
                                        {t('pages.adminTripsDaily.status.soon')}
                                    </Badge>
                                    <h5>{t('sidebar.weatherDailyAnalysisMenu.specific')}</h5>
                                    <p className="mb-0 text-muted">
                                        {t('sidebar.weatherDailyAnalysisMenu.specificPlaceholder')}
                                    </p>
                                </div>
                            </Tab.Pane>
                            <Tab.Pane eventKey={TABS.BULK_OBSERVATION_STANDARD}>
                                <StandardBulkObservationCheckTab />
                            </Tab.Pane>
                            <Tab.Pane eventKey={TABS.BULK_OBSERVATION_FALLBACK}>
                                <StandardBulkObservationCheckTab
                                    prefix="pages.adminWeatherDailyAnalysis.standardBulkObservationFallback"
                                    runRequest={checkStandardScheduledBulkObservationFallback}
                                    sourceField="obs_data_source_fallback"
                                    endpointPath="/api/standard/trips/scheduled/bulk-observation-check/fallback/grouped-by-location"
                                />
                            </Tab.Pane>
                            <Tab.Pane eventKey={TABS.OBSERVATION_SPECIFIC_PLACEHOLDER}>
                                <div className="tn-slot-placeholder">
                                    <FiEye size={28} className="mb-3 opacity-50" aria-hidden />
                                    <Badge bg="secondary" className="tn-status-badge mb-3">
                                        {t('pages.adminTripsDaily.status.soon')}
                                    </Badge>
                                    <h5>{t('sidebar.weatherDailyAnalysisMenu.observations')}</h5>
                                    <p className="mb-0 text-muted">
                                        {t('sidebar.weatherDailyAnalysisMenu.observationSpecificPlaceholder')}
                                    </p>
                                </div>
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminWeatherDailyAnalysis;
